import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { v4 as uuidv4 } from "uuid";
import { Events, Params, Screens, useAnalytics } from "../../../AppAnalyticsProvider";
import { useAppState } from "../../../AppListenerProvider";
import { LocationOnboarding } from "../../../models/locationOnboarding";
import { OnboardingStatus } from "../../../models/onboardingStatus";
import { OnboardingStepConfig } from "../../../models/onboardingStepConfig";
import { SkillLevels, Terrain } from "../../../models/signUpData";
import { SkillLevel } from "../../../models/skillLevel";
import LoadingSplash from "../../../pages/LoadingSplash";
import LocationService from "../../../services/location.service";
import PreferencesService from "../../../services/preferences.service";
import UserService from "../../../services/user.service";
import BirthdayStep from "../BirthdayStep/BirthdayStep";
import LocationPermissions from "../LocationPermissions/LocationPermissions";
import LocationSearch from "../LocationSearch/LocationSearch";
import NameInput from "../NameInput/NameInput";

import { useSignUp } from "../SignUpProvider/SignUpProvider";
import SportSelection from "../SportSelection/SportSelection";
import SportSkillSelection from "../SportSkillTerrainSelection/SportSkillTerrainSelection";
import UsernameInput from "../UsernameInput/UsernameInput";
import WelcomeEclipticLoading from "../WelcomeEcliptic/WelcomeEclipticLoading";
import WelcomeSignUpAuth from "../WelcomeSignUpAuth/WelcomeSignUpAuth";
import WelcomeSignUpEmail from "../WelcomeSignUpEmail/WelcomeSignUpEmail";

interface ContainerProps {
}

const WelcomeSignUp: React.FC<ContainerProps> = ({ }) => {
    const history = useHistory();
    const { trackScreen, trackSignupStep, trackEvent } = useAnalytics();
    const { isNative, platform } = useAppState();
    const { signUpData, updateSignUpData, step, setStep, resetSignUp } = useSignUp();
    const [defaultLocations, setDefaultLocations] = useState<LocationOnboarding[]>([]);
    const [googlePlacesSessionToken, setGooglePlacesSessionToken] = useState<string | null>(null);

    const stepConfigState: Record<string, OnboardingStepConfig> = {
        [OnboardingStatus.IN_PROGRESS_SIGNUP_INIT]: {
            titleText: '',
            showBackButton: false,
            showSkipButton: false,
        },
        [OnboardingStatus.IN_PROGRESS_SIGNUP_BIRTHDAY]: {
            titleText: 'Enter your birthday 🥳',
            showSkipButton: false,
            showBackButton: true,
            onBack: async () => {
                await navigateBackToInitial();
            },
            onNext: async () => {
                await handleOnboardingStatus(OnboardingStatus.IN_PROGRESS_SIGNUP_HUMAN_NAME);
            },
        },
        [OnboardingStatus.IN_PROGRESS_SIGNUP_HUMAN_NAME]: {
            titleText: "What's your name?",
            showSkipButton: false,
            showBackButton: true,
            onBack: () => handleOnboardingStatus(OnboardingStatus.IN_PROGRESS_SIGNUP_BIRTHDAY),
            onNext: async () => {
                await handleOnboardingStatus(OnboardingStatus.IN_PROGRESS_SIGNUP_NAME)
            }
        },
        [OnboardingStatus.IN_PROGRESS_SIGNUP_NAME]: {
            titleText: `${signUpData.firstName}, pick your username`,
            showSkipButton: false,
            showBackButton: true,
            onBack: () => handleOnboardingStatus(OnboardingStatus.IN_PROGRESS_SIGNUP_HUMAN_NAME),
            onNext: async () => {
                await handleOnboardingStatus(OnboardingStatus.IN_PROGRESS_SIGNUP_SPORTS);
            }
        },
        [OnboardingStatus.IN_PROGRESS_SIGNUP_SPORTS]: {
            titleText: `${signUpData.username}, choose your sports`,
            showSkipButton: false,
            showBackButton: true,
            onBack: () => handleOnboardingStatus(OnboardingStatus.IN_PROGRESS_SIGNUP_NAME),
            onNext: async () => {
                await handleOnboardingStatus(OnboardingStatus.IN_PROGRESS_SIGNUP_SPORTS_SKILL_TERRAIN);
            }
        },
        [OnboardingStatus.IN_PROGRESS_SIGNUP_SPORTS_SKILL_TERRAIN]: {
            titleText: signUpData.sports.length > 0 ? signUpData.displaySkill ? `What's your ${signUpData.sports[signUpData.sportIndex].toLowerCase()} skill level?` : `What terrain are you hitting when ${signUpData.sports[signUpData.sportIndex].toLowerCase()}?` : signUpData.displaySkill ? `What's your skill level?` : `What terrain are you hitting?`,
            showSkipButton: true,
            showBackButton: true,
            onBack: async () => {
                await updateSignUpData({ sportIndex: 0, displaySkill: true, terrain: null, skills: null });
                await handleOnboardingStatus(OnboardingStatus.IN_PROGRESS_SIGNUP_SPORTS)
            },
            onSkip: async () => {
                trackEvent(Events.SIGNUP_STEP_SKIPPED, {
                    [Params.SCREEN_NAME]: Screens.SIGNUP_SPORT_SKILL_TERRAIN,
                    [Params.STEP_NUMBER]: getStepNumber(OnboardingStatus.IN_PROGRESS_SIGNUP_SPORTS_SKILL_TERRAIN),
                });
                if (signUpData.sports.length > 0) {
                    // Retrieve existing skills and terrain
                    const existingSkills = signUpData.skills || {};
                    const existingTerrain = signUpData.terrain || {};

                    // Update skill and terrain only for skipped sports
                    const newSkills = signUpData.sports.reduce((acc: SkillLevels, sport) => {
                        if (!existingSkills[sport]) {
                            acc[sport] = SkillLevel.FAN;
                        } else {
                            acc[sport] = existingSkills[sport];
                        }
                        return acc;
                    }, {});

                    const newTerrain = signUpData.sports.reduce((acc: Terrain, sport) => {
                        if (!existingTerrain[sport]) {
                            acc[sport] = [];
                        } else {
                            acc[sport] = existingTerrain[sport];
                        }
                        return acc;
                    }, {});

                    await updateSignUpData({
                        skills: { ...existingSkills, ...newSkills },
                        terrain: { ...existingTerrain, ...newTerrain }
                    });
                }
                await handleOnboardingStatus(OnboardingStatus.IN_PROGRESS_SIGNUP_LOCATION_PROMPT)
            },
            onNext: async () => {
                await handleOnboardingStatus(OnboardingStatus.IN_PROGRESS_SIGNUP_LOCATION_PROMPT);
            }
        },
        [OnboardingStatus.IN_PROGRESS_SIGNUP_LOCATION_PROMPT]: {
            titleText: 'Tell us your home base',
            showSkipButton: true,
            showBackButton: true,
            onBack: async () => {
                await updateSignUpData({ displaySkill: true });
                await handleOnboardingStatus(OnboardingStatus.IN_PROGRESS_SIGNUP_SPORTS_SKILL_TERRAIN)
            },
            onSkip: async () => {
                trackEvent(Events.SIGNUP_STEP_SKIPPED, {
                    [Params.SCREEN_NAME]: Screens.SIGNUP_LOCATION_PERMISSION,
                    [Params.STEP_NUMBER]: getStepNumber(OnboardingStatus.IN_PROGRESS_SIGNUP_LOCATION_PROMPT),
                });
                await handleOnboardingStatus(OnboardingStatus.IN_PROGRESS_SIGNUP_LOCATION_SEARCH);
            },
            onNext: async () => {
                await handleOnboardingStatus(OnboardingStatus.IN_PROGRESS_SIGNUP_LOCATION_SEARCH);
            },
            onSkipDelay: 1000,
        },
        [OnboardingStatus.IN_PROGRESS_SIGNUP_LOCATION_SEARCH]: {
            titleText: 'Search for your base',
            showSkipButton: true,
            showBackButton: true,
            onBack: async () => {
                await handleOnboardingStatus(OnboardingStatus.IN_PROGRESS_SIGNUP_LOCATION_PROMPT);
            },
            onSkip: async () => {
                trackEvent(Events.SIGNUP_STEP_SKIPPED, {
                    [Params.SCREEN_NAME]: Screens.SIGNUP_LOCATION_SEARCH,
                    [Params.STEP_NUMBER]: getStepNumber(OnboardingStatus.IN_PROGRESS_SIGNUP_LOCATION_SEARCH),
                });
                await handleOnboardingStatus(OnboardingStatus.IN_PROGRESS_SIGNUP_AUTH);
            },
            onNext: async () => {
                await handleOnboardingStatus(OnboardingStatus.IN_PROGRESS_SIGNUP_AUTH);
            }
        },
        [OnboardingStatus.IN_PROGRESS_SIGNUP_AUTH]: {
            titleText: 'Finish Signing Up for Ecliptic!',
            showBackButton: true,
            showSkipButton: false,
            onBack: async () => {
                await handleOnboardingStatus(OnboardingStatus.IN_PROGRESS_SIGNUP_LOCATION_SEARCH);
            },
            onNext: async () => {
                resetSignUp();
                history.push("/");
                await handleOnboardingStatus(OnboardingStatus.COMPLETED);
            }
        },
        [OnboardingStatus.IN_PROGRESS_SIGNUP_USERNAME]: {
            titleText: 'Provide an Email and Password',
            showBackButton: true,
            showSkipButton: false,
            onBack: async () => {
                await handleOnboardingStatus(OnboardingStatus.IN_PROGRESS_SIGNUP_AUTH)
            },
            onNext: async () => {
                resetSignUp();
                history.push("/");
                await handleOnboardingStatus(OnboardingStatus.COMPLETED);
            }
        },
    };

    const navigateBackToInitial = async () => {
        await handleOnboardingStatus(OnboardingStatus.INIT);
        history.push('/flow');
    }

    const handleOnboardingStatus = async (status: string) => {
        const newStepNumber = getStepNumber(status);
        const currentStepNumber = getStepNumber(step);
        const screenName = getScreenNameForStep(status);
        const currentStepName = getScreenNameForStep(step);

        // Track back navigation (going to a lower step number)
        // Fire-and-forget - don't block state update
        if (newStepNumber < currentStepNumber) {
            trackEvent(Events.SIGNUP_STEP_BACK, {
                [Params.SCREEN_NAME]: currentStepName,
                [Params.STEP_NUMBER]: currentStepNumber
            });
        }

        // Track the new step - that's all we need for funnel analysis
        // If user sees step 5, it means they completed steps 1-4
        // Some steps (like skill/terrain) are tracked in their own component with more detail
        // Fire-and-forget - don't block state update
        if (screenName) {
            trackSignupStep(screenName, status, {
                [Params.STEP_NUMBER]: newStepNumber
            });
        }

        // Signup completion — the conversion event for the onboarding funnel.
        // Also fires REFERRAL_SIGNUP_ATTRIBUTED if the user arrived via a
        // /share?username=X deep link (referrer stored in preferences by
        // DeepLinkProvider). Together these unlock k-factor measurement.
        if (status === OnboardingStatus.COMPLETED) {
            trackEvent(Events.SIGNUP_COMPLETE, {});

            const referrer = await PreferencesService.getReferrer();
            if (referrer) {
                trackEvent(Events.REFERRAL_SIGNUP_ATTRIBUTED, {
                    // Logged but not registered as a GA4 dim — PII / high-cardinality.
                    // The referral dashboard will aggregate the count; drill-down
                    // uses the value via event parameter inspection.
                    [Params.REFERRER_USERNAME]: referrer,
                    [Params.LINK_TYPE]: 'referral',
                });
            }
        }

        await setStep(status);
    }

    const getStepNumber = (step: string): number => {
        const stepOrder: Record<string, number> = {
            [OnboardingStatus.IN_PROGRESS_SIGNUP_INIT]: 1,
            [OnboardingStatus.IN_PROGRESS_SIGNUP_BIRTHDAY]: 2,
            [OnboardingStatus.IN_PROGRESS_SIGNUP_HUMAN_NAME]: 3,
            [OnboardingStatus.IN_PROGRESS_SIGNUP_NAME]: 4,
            [OnboardingStatus.IN_PROGRESS_SIGNUP_SPORTS]: 5,
            [OnboardingStatus.IN_PROGRESS_SIGNUP_SPORTS_SKILL_TERRAIN]: 6,
            [OnboardingStatus.IN_PROGRESS_SIGNUP_LOCATION_PROMPT]: 7,
            [OnboardingStatus.IN_PROGRESS_SIGNUP_LOCATION_SEARCH]: 8,
            [OnboardingStatus.IN_PROGRESS_SIGNUP_AUTH]: 9,
            [OnboardingStatus.IN_PROGRESS_SIGNUP_USERNAME]: 10
        };
        return stepOrder[step] || 0;
    };

    const getScreenNameForStep = (step: string): string | null => {
        switch (step) {
            case OnboardingStatus.IN_PROGRESS_SIGNUP_INIT:
                return Screens.SIGNUP_WELCOME;
            case OnboardingStatus.IN_PROGRESS_SIGNUP_BIRTHDAY:
                return Screens.SIGNUP_BIRTHDAY;
            case OnboardingStatus.IN_PROGRESS_SIGNUP_HUMAN_NAME:
                return Screens.SIGNUP_NAME;
            case OnboardingStatus.IN_PROGRESS_SIGNUP_NAME:
                return Screens.SIGNUP_USERNAME;
            case OnboardingStatus.IN_PROGRESS_SIGNUP_SPORTS:
                return Screens.SIGNUP_SPORTS;
            case OnboardingStatus.IN_PROGRESS_SIGNUP_SPORTS_SKILL_TERRAIN:
                // Tracked granularly in SportSkillTerrainSelection component with sport-specific details
                return null;
            case OnboardingStatus.IN_PROGRESS_SIGNUP_LOCATION_PROMPT:
                return Screens.SIGNUP_LOCATION_PERMISSION;
            case OnboardingStatus.IN_PROGRESS_SIGNUP_LOCATION_SEARCH:
                return Screens.SIGNUP_LOCATION_SEARCH;
            case OnboardingStatus.IN_PROGRESS_SIGNUP_AUTH:
                return Screens.SIGNUP_AUTH;
            case OnboardingStatus.IN_PROGRESS_SIGNUP_USERNAME:
                return Screens.SIGNUP_EMAIL_PASSWORD;
            case OnboardingStatus.COMPLETED:
                return Screens.SIGNUP_COMPLETED;
            default:
                return Screens.SIGNUP_WELCOME;
        }
    }

    // Track initial signup start
    useEffect(() => {
        trackScreen(Screens.SIGNUP_WELCOME);
    }, []);

    useEffect(() => {
        const currGooglePlacesToken = uuidv4();
        console.log("Setting Google Places Token: " + currGooglePlacesToken);
        setGooglePlacesSessionToken(currGooglePlacesToken);
    }, []);

    useEffect(() => {
        const initializeStep = async () => {
            if (step) {
                if (step.includes(OnboardingStatus.SIGNUP)) {
                    await handleOnboardingStatus(step);
                } else {
                    console.log("INVALID STATUS: GOING TO FLOW");
                    await handleOnboardingStatus(OnboardingStatus.IN_PROGRESS_SIGNUP_INIT);
                }
            } else {
                await handleOnboardingStatus(OnboardingStatus.INIT);
                history.push("/flow");
            }
        };

        console.log("Loading Sign Up Flow App Page");
        initializeStep();
    }, []);  // Empty dependency array means this runs once on mount

    // Step changes are now tracked in handleOnboardingStatus via trackSignupStep

    useEffect(() => {
        const loadRelevantLocations = async () => {
            if (signUpData.latitude && signUpData.longitude && googlePlacesSessionToken) {
                const country = await LocationService.getCountryFromLocationAPI(signUpData.latitude, signUpData.longitude, googlePlacesSessionToken);
                if (country) {
                    await updateSignUpData({ countryData: country });
                }

                console.log("LOADING RELEVANT LOCATIONS");
                try {
                    const locations: LocationOnboarding[] = await UserService.getUserLocationsOnboardingLocation(1, signUpData.latitude, signUpData.longitude);
                    setDefaultLocations(locations);
                } catch (error) {
                    console.error("Error loading locations: ", error);
                    setDefaultLocations([]);
                }
            } else {
                console.log("LOADING DEFAULT LOCATIONS");
                try {
                    const locations: LocationOnboarding[] = await UserService.getUserLocationsOnboarding(1);
                    setDefaultLocations(locations);
                } catch (error) {
                    console.error("Error loading locations: ", error);
                    setDefaultLocations([]);
                }
            }
        }

        loadRelevantLocations();
    }, [signUpData.latitude, signUpData.longitude, googlePlacesSessionToken]);

    useEffect(() => {
        const redirectBasedOnStep = async () => {
            console.log("Step: " + step);
            if (isNative && step === OnboardingStatus.IN_PROGRESS_SIGNUP_INIT) {
                await handleOnboardingStatus(OnboardingStatus.IN_PROGRESS_SIGNUP_BIRTHDAY);
            }

            if (!isNative && step === OnboardingStatus.IN_PROGRESS_SIGNUP_INIT) {
                await handleOnboardingStatus(OnboardingStatus.IN_PROGRESS_SIGNUP_BIRTHDAY);
            }
        };

        redirectBasedOnStep();
    }, [step, isNative]);


    const renderStep = () => {
        // State Change Controlled In This Logic
        switch (step) {
            case OnboardingStatus.IN_PROGRESS_SIGNUP_BIRTHDAY:
                console.log("Rendering Birthday Step");
                return (
                    <BirthdayStep
                        showSkipButton={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_BIRTHDAY].showSkipButton}
                        showBackButton={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_BIRTHDAY].showBackButton}
                        title={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_BIRTHDAY].titleText}
                        onNext={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_BIRTHDAY].onNext}
                        onBack={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_BIRTHDAY].onBack} />
                );
            case OnboardingStatus.IN_PROGRESS_SIGNUP_HUMAN_NAME:
                return (
                    <NameInput
                        showSkipButton={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_HUMAN_NAME].showSkipButton}
                        showBackButton={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_HUMAN_NAME].showBackButton}
                        onBack={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_HUMAN_NAME].onBack}
                        title={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_HUMAN_NAME].titleText}
                        onNext={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_HUMAN_NAME].onNext} />
                );
            case OnboardingStatus.IN_PROGRESS_SIGNUP_NAME:
                return (
                    <UsernameInput
                        showBackButton={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_NAME].showBackButton}
                        showSkipButton={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_NAME].showSkipButton}
                        title={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_NAME].titleText}
                        onBack={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_NAME].onBack}
                        onNext={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_NAME].onNext}
                    />
                );
            case OnboardingStatus.IN_PROGRESS_SIGNUP_SPORTS:
                return (
                    <SportSelection
                        showBackButton={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_SPORTS].showBackButton}
                        showSkipButton={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_SPORTS].showSkipButton}
                        title={`${signUpData.username}, choose your sports`}
                        onBack={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_SPORTS].onBack}
                        onNext={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_SPORTS].onNext} />
                );
            case OnboardingStatus.IN_PROGRESS_SIGNUP_SPORTS_SKILL_TERRAIN:
                return (
                    <SportSkillSelection
                        showBackButton={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_SPORTS_SKILL_TERRAIN].showBackButton}
                        showSkipButton={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_SPORTS_SKILL_TERRAIN].showSkipButton}
                        title={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_SPORTS_SKILL_TERRAIN].titleText}
                        onBack={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_SPORTS_SKILL_TERRAIN].onBack}
                        onSkip={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_SPORTS_SKILL_TERRAIN].onSkip}
                        onNext={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_SPORTS_SKILL_TERRAIN].onNext}
                    />
                )
            case OnboardingStatus.IN_PROGRESS_SIGNUP_LOCATION_PROMPT:
                return (
                    <LocationPermissions
                        showBackButton={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_LOCATION_PROMPT].showBackButton}
                        showSkipButton={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_LOCATION_PROMPT].showSkipButton}
                        title={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_LOCATION_PROMPT].titleText}
                        onBack={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_LOCATION_PROMPT].onBack}
                        onSkip={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_LOCATION_PROMPT].onSkip}
                        onSkipDelay={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_LOCATION_PROMPT].onSkipDelay}
                        onNext={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_LOCATION_PROMPT].onNext} />
                )
            case OnboardingStatus.IN_PROGRESS_SIGNUP_LOCATION_SEARCH:
                return (
                    <LocationSearch
                        onBack={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_LOCATION_SEARCH].onBack}
                        defaultLocations={defaultLocations}
                        defaultIsLoading={defaultLocations.length === 0}
                        onNext={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_LOCATION_SEARCH].onNext}
                        onSkip={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_LOCATION_SEARCH].onSkip}
                        sessionToken={googlePlacesSessionToken} />
                )
            case OnboardingStatus.IN_PROGRESS_SIGNUP_PHONE_NUMBER:
                // Phone step removed from onboarding — redirect users who had this step saved
                handleOnboardingStatus(OnboardingStatus.IN_PROGRESS_SIGNUP_AUTH);
                return <WelcomeEclipticLoading />;
            case OnboardingStatus.IN_PROGRESS_SIGNUP_AUTH:
                return (
                    <WelcomeSignUpAuth
                        handleAuthedStep={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_AUTH].onNext}
                        handleEmailAuthStep={() => handleOnboardingStatus(OnboardingStatus.IN_PROGRESS_SIGNUP_USERNAME)}
                        showBackButton={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_AUTH].showBackButton}
                        showSkipButton={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_AUTH].showSkipButton}
                        title={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_AUTH].titleText}
                        onBack={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_AUTH].onBack}
                    />
                )
            case OnboardingStatus.IN_PROGRESS_SIGNUP_USERNAME:
                return (
                    <WelcomeSignUpEmail
                        handleSignUpGoBack={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_USERNAME].onBack}
                        handleSignUpLogin={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_USERNAME].onNext}
                        title={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_USERNAME].titleText}
                        showBackButton={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_USERNAME].showBackButton}
                        showSkipButton={stepConfigState[OnboardingStatus.IN_PROGRESS_SIGNUP_USERNAME].showSkipButton}
                    />
                );
            case OnboardingStatus.COMPLETED:
                return (
                    <LoadingSplash />
                );
            default:
                return <WelcomeEclipticLoading />;
        }
    }

    return renderStep();
};

export default WelcomeSignUp;